<template>
  <div :class="{ header: !isCart, 'header-cart': isCart }">
    <div class="header-inner">
      <div class="menu-left">
        <div class="hamburger-icon" @click="toggleMenu" data-test="mobile-top-nav-hamburger-icon">
          <font-awesome-icon icon="fa-regular fa-bars-sort" class="tw-text-[20px]" />
        </div>
        <router-link :to="{ name: homeRoute }" data-test="mobile-top-nav-logo">
          <img :src="logoSrc" class="mobile-icon" />
        </router-link>
      </div>
      <div class="header-menu-options">
        <div class="header-menu-icons"></div>
        <DropDown :items="userMenuItems" v-if="user" testId="mobile-user-drop-down-menu">
          <div class="header-menu-user-options" tabindex="10">
            <div class="avatar-circle">
              <div class="avatar-initials">{{ userInitials }}</div>
            </div>
            <div class="tw-flex tw-flex-row tw-items-center">
              <div class="tw-mr-1.5">User Menu</div>
              <font-awesome-icon icon="fa-solid fa-caret-down" />
            </div>
          </div>
        </DropDown>
      </div>
    </div>

    <div
      :class="[
        'left-nav-mobile',
        { 'mobile-menu-slider': !isCart, 'mobile-menu-slider-cart': isCart, open: menuIsOpen },
      ]"
    >
      <div class="close-x" @click="closeMenu" data-test="mobile-left-nav-close">
        <font-awesome-icon icon="fa-solid fa-xmark" class="tw-text-[20px]" />
      </div>
      <div class="mobile-client-info" v-if="client">
        <div class="client-info">
          Managed by<br />
          {{ client.name }}
        </div>
        <div class="account-info">
          <div class="account-id">Account ID: {{ client.id }}</div>
          <div class="account-pin tw-items-center">
            PIN: {{ pin }}
            <IconButton
              @click="toggleShowPin"
              :icon="{
                default: { icon: `fa-regular ${showPin ? 'fa-eye-slash' : 'fa-eye'}` },
                hover: { icon: `fa-solid ${showPin ? 'fa-eye-slash' : 'fa-eye'}` },
              }"
              class="pinToggle tw-text-[18px]"
              data-test="mobile-show-account-pin"
            />
          </div>
        </div>
      </div>
      <div :class="{ 'items-container': !isCart, 'items-container-cart': isCart }">
        <div v-if="!loading" class="left-nav-items">
          <NavSearch
            v-if="isSearchable"
            v-model="search"
            class="tw-mt-4 tw-mb-2 tw-mx-3"
            test-id="mobile-search-nav"
          />
          <div
            class="left-nav-item left-nav-back"
            v-if="back"
            @click="back.action"
            data-test="mobile-nav-back-button"
          >
            <font-awesome-icon
              icon="fa-solid fa-chevron-left"
              class="nav-icon tw-text-[16px] tw-w-4"
            />
            <div class="nav-text">
              {{ back.name ? `${back.name}` : "Go Back" }}
            </div>
          </div>
          <NavTopMobileItem
            v-for="(item, i) in filteredMenuItems"
            :key="i"
            :item="item"
            @close="closeMenu"
          />
          <div v-if="hasAdvanced">
            <IconButton
              testId="mobile-left-nav-show-more-less-btn"
              @click="toggleShowAdvanced"
              contentPosition="right"
              class="show-more-button tw-flex tw-items-center tw-px-4 tw-py-2 tw-w-full"
              :icon="{
                default: {
                  icon: 'fa-regular fa-chevron-down',
                  flip: showAdvanced ? 'vertical' : undefined,
                  class: 'tw-w-4',
                },
                hover: {
                  icon: 'fa-solid fa-chevron-down',
                  flip: showAdvanced ? 'vertical' : undefined,
                  class: 'tw-w-4',
                },
              }"
            >
              <div class="tw-text-sm tw-ml-2">Show {{ showAdvanced ? "Less" : "More" }}</div>
            </IconButton>
          </div>
          <NavSearchNoResults v-if="isSearchable && filteredMenuItems.length === 0" />
        </div>

        <div v-if="loading" class="left-nav-items">
          <div class="loading-block-container">
            <div class="loading-block nav-loading-block-left"></div>
            <div class="loading-block nav-loading-block-right"></div>
          </div>
          <div class="loading-block-container">
            <div class="loading-block nav-loading-block-left"></div>
            <div class="loading-block nav-loading-block-right"></div>
          </div>
          <div class="loading-block-container">
            <div class="loading-block nav-loading-block-left"></div>
            <div class="loading-block nav-loading-block-right"></div>
          </div>
          <div class="loading-block-container">
            <div class="loading-block nav-loading-block-left"></div>
            <div class="loading-block nav-loading-block-right"></div>
          </div>
        </div>
      </div>
      <div
        class="left-nav-portal-switch tw-px-4 tw-py-2"
        :class="{ 'left-nav-portal-switch-cart': isCart }"
      >
        <PortalSwitch />
      </div>
      <div :class="{ 'left-nav-footer': !isCart, 'left-nav-footer-cart': isCart }">
        <div class="left-nav-footer-help">Let Us Help</div>
        <div class="left-nav-footer-icons">
          <NavLeftFooterIcon
            v-for="(helper, index) in helpItems"
            :key="index"
            class="left-nav-icon-container"
            @click="helper.action"
            :data-test="`mobile-nav-left-help-${formatDataTest(helper.icon?.default?.icon)}`"
            :icon="helper.icon"
          />
        </div>
      </div>
    </div>

    <div class="mobile-menu-sub-slider" :class="{ open: subMenuIsOpen }">Here is a submenu</div>
    <transition name="fade">
      <div class="mobile-menu-underlay" v-if="menuIsOpen" @click="closeMenu"></div>
    </transition>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import constants from "@/utils/constants";
import DropDown from "@/components/display/DropDown";
import { fuzzy } from "fast-fuzzy";
import IconButton from "@/components/display/IconButton.vue";
import NavLeftFooterIcon from "@/components/nav/NavLeftFooterIcon.vue";
import NavLink from "@/classes/NavLink";
import NavSearch from "@/components/nav/NavSearch";
import NavSearchNoResults from "@/components/nav/NavSearchNoResults";
import NavTopMobileItem from "@/components/nav/NavTopMobileItem.vue";
import PortalSwitch from "@/components/smart/PortalSwitch";
import router from "@/router";
import store from "@/store";

export default {
  name: "NavTopMobile",
  components: {
    DropDown,
    IconButton,
    NavLeftFooterIcon,
    NavSearch,
    NavSearchNoResults,
    NavTopMobileItem,
    PortalSwitch,
  },
  props: {
    user: Object,
    client: Object,
    userMenuItems: {
      validator: (value) => {
        if (!Array.isArray(value)) {
          return false;
        }

        return value.filter((item) => !(item instanceof NavLink)).length === 0;
      },
    },
    homeRoute: {
      type: String,
      default: constants.ROUTES.HOME,
    },
    back: NavLink,
    menuTitle: String,
    menuItems: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    helpItems: {
      validator: (value) => {
        if (!Array.isArray(value)) {
          return false;
        }

        return value.filter((item) => !item.icon || !item.action).length === 0;
      },
    },
  },
  setup(props, { emit }) {
    const menuIsOpen = ref(false);
    const subMenuIsOpen = ref(false);

    const search = ref(null);
    const showAdvanced = ref(false);

    const theme = computed(() => store.getters["theme/get"]);
    const logoSrc = computed(() => {
      return {
        default: require("@/assets/Nx-1c-BLACK-Logo-N.svg"),
        dark: require("@/assets/Nx-1c-WHITE-Logo-N.svg"),
      }[theme.value.stylesheet];
    });

    const filteredMenuItems = computed(() => {
      if (!search.value) {
        return showAdvanced.value
          ? props.menuItems
          : props.menuItems.filter((item) => !item.advanced || routeMatches(item.routeName));
      }
      return props.menuItems.filter((menuItem) => {
        let match = false;
        const keywords = menuItem?.keywords || [];
        keywords.forEach((keyword) => {
          if (fuzzy(search.value, keyword, { ignoreSymbols: false }) >= 0.8) {
            match = true;
          }
        });
        return match;
      });
    });
    const hasAdvanced = computed(
      () => props.menuItems.filter((menuItem) => menuItem.advanced).length > 0
    );
    const isSearchable = computed(
      () => props.menuItems.filter((menuItem) => menuItem.keywords.length > 0).length > 0
    );

    const toggleMenu = () => {
      menuIsOpen.value = !menuIsOpen.value;
      if (!menuIsOpen.value && subMenuIsOpen.value) {
        subMenuIsOpen.value = false;
      }
      emit("value", menuIsOpen.value);
    };

    const closeMenu = () => {
      search.value = null;
      menuIsOpen.value = false;
      subMenuIsOpen.value = false;
    };

    const userInitials = computed(() => {
      if (props.user) {
        if (props.user.firstName && props.user.lastName) {
          return props.user.firstName.slice(0, 1) + props.user.lastName.slice(0, 1);
        }
        return props.user.email.slice(0, 1);
      }
      return null;
    });

    // PIN functionality
    const showPin = ref(false);
    const pin = computed(() => {
      if (showPin.value) {
        return props.client.pin;
      }
      return "• • • • • •";
    });

    const toggleShowPin = () => {
      showPin.value = !showPin.value;
    };

    const formatDataTest = (input) => input.toLowerCase().replace(/(,|_| |;)/g, "-");

    const routeMatches = (routeName) => {
      const route = router?.currentRoute?.value;
      const matchedRoutes = [...route.matched];
      const match = matchedRoutes.reverse().find((route) => routeName === route?.name);
      return !!match;
    };

    const toggleShowAdvanced = () => (showAdvanced.value = !showAdvanced.value);

    const isCart = computed(() => router?.currentRoute?.value?.meta?.cart);

    return {
      logoSrc,
      closeMenu,
      filteredMenuItems,
      formatDataTest,
      hasAdvanced,
      isSearchable,
      menuIsOpen,
      pin,
      routeMatches,
      search,
      showAdvanced,
      showPin,
      subMenuIsOpen,
      toggleMenu,
      toggleShowAdvanced,
      toggleShowPin,
      userInitials,
      isCart,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
$menuwidth: 230px;
$nav-item-height-mobile: 45px;

.header,
.header-cart {
  // height: $header-height;
  font-size: 14px;
  z-index: 900;

  .header-inner {
    padding: 0 0 0 $grid-gutter-width;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .menu-left {
      display: flex;
      align-items: center;

      .hamburger-icon {
        cursor: pointer;
      }
      .mobile-icon {
        margin-left: 2rem;
        width: 38px;
        cursor: pointer;
      }
    }
  }

  .header-client-container {
    font-size: 0.75rem;
  }

  .left-nav-mobile {
    // width: 215px;
    display: flex;
    flex-direction: column;
    height: 100%;
    // justify-content: space-between;

    // .logo {
    //   margin-bottom: 3rem;

    //   &.with-back {
    //     margin-bottom: 1rem;
    //   }
    // }

    .mobile-client-info {
      font-size: 0.75rem;
      padding: 0 1.5rem 1rem;

      .client-info {
        margin-bottom: 1rem;
      }

      .account-info {
        .account-id {
          margin-bottom: 1rem;
        }
        .account-pin {
          display: flex;
          .pinToggle {
            margin-left: 1rem;
          }
        }
      }
    }

    .items-container,
    .items-container-cart {
      flex-grow: 1;
      overflow-x: hidden;
      overflow-y: auto;
      // scroll-snap-type: y mandatory;
      justify-content: space-between;
      display: flex;
      flex-direction: column;

      /* Works on Chrome/Edge/Safari */
      // &::-webkit-scrollbar {
      //   width: 12px;
      // }
      // &::-webkit-scrollbar-thumb {
      //   border-radius: 5px;
      //   border-width: 2px;
      //   border-style: solid;
      // }

      /* Works on Firefox */
      // scrollbar-width: thin;

      .left-nav-title {
        font-size: 0.75rem;
        text-transform: uppercase;
        display: flex;
        flex-direction: row;
        align-items: center;
        // margin-bottom: 1rem;
        height: $nav-item-height-mobile;
        font-weight: 600;
        padding-left: 24px;
      }

      .left-nav-item {
        font-size: 14px;
        display: flex;
        flex-direction: row;
        align-items: center;
        height: $nav-item-height-mobile;
        cursor: pointer;
        padding-left: $nav-padding-side;
        scroll-snap-align: end;
        text-decoration: none;

        &:hover {
          text-decoration: none;
        }

        .nav-icon {
          margin-right: 1rem;
        }

        @-webkit-keyframes fadeInAppear {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes fadeInAppear {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        &.advanced {
          -webkit-animation-name: fadeInAppear;
          animation-name: fadeInAppear;
          -webkit-animation-timing-function: ease-in;
          animation-timing-function: ease-in;
          -webkit-animation-duration: 0.5s;
          animation-duration: 0.5s;
          -webkit-animation-delay: 0s;
          animation-delay: 0s;
          -webkit-animation-fill-mode: forwards;
          animation-fill-mode: forwards;
          opacity: 0;
        }
      }

      .left-nav-back {
        font-size: 0.75rem;

        .nav-text {
          text-transform: uppercase;
        }
      }
    }

    .left-nav-footer,
    .left-nav-footer-cart {
      height: 80px;
      flex-grow: 0;
      font-size: 0.75rem;
      padding: 16px $nav-padding-side;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      text-align: center;
      .left-nav-footer-help {
        margin-bottom: 0.5rem;
      }
      .left-nav-footer-icons {
        display: flex;
        align-content: center;
        justify-content: space-around;
      }
    }
  }

  .header-menu-options {
    display: flex;

    .header-menu-icons {
      display: flex;
      align-items: center;
      margin-right: 1rem;

      .header-menu-icon {
        svg {
          padding: 5px;
        }
        &.active {
          border-radius: $border-radius;
        }
        &:hover {
          border-radius: $border-radius;
        }
      }
    }

    .header-menu-user-options {
      display: flex;
      flex-direction: row;
      position: relative;
      height: 100%;
      align-items: center;
      cursor: pointer;
      padding: 9px;

      &:focus {
        outline: none;
      }

      .header-menu-list {
        position: absolute;
        display: none;
      }

      .avatar-circle {
        width: 2rem;
        height: 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        border-width: 1px;
        border-style: solid;
        border-radius: 1rem;
        margin-right: 12px;
        .avatar-initials {
          text-transform: uppercase;
        }
      }

      .greeting {
        display: flex;
        flex-direction: row;
        align-items: center;

        .name {
          margin-right: 6px;
        }
      }

      &:hover,
      &:focus,
      &.forceshow {
        .header-menu-list {
          display: block;
          border-radius: $border-radius;
          width: 200px;
          top: 100%;
          right: 0;
          padding: 12px 0;
          z-index: 100;

          .menu-list-item {
            padding: 7px 14px;
            font-size: 0.875rem;
            text-decoration: none;
            width: 100%;
            display: block;

            &:hover {
              text-decoration: none;
            }
          }
        }
      }
    }
  }

  .mobile-menu-slider,
  .mobile-menu-slider-cart {
    position: fixed;
    left: 0;
    transform: translateX(-1.25 * $menuwidth);
    top: 0;
    height: 100%;
    z-index: 1000;
    transition: transform 0.5s ease;
    width: $menuwidth;
    display: flex;
    flex-direction: column;
    // background-color: $epsilon;
    box-shadow: 0 0 5px 0;

    &.open {
      transform: translateX(0);
    }

    .close-x {
      cursor: pointer;
      padding: 1.5rem;
    }
  }

  .mobile-menu-sub-slider {
    transform: translateX(-1.25 * $menuwidth);
    width: calc(100% - #{$menuwidth});
    max-width: $menuwidth;
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    // background-color: blue;
    z-index: 975;
    transition: transform 0.5s ease;
    box-shadow: 0 0 5px 0;

    &.open {
      transform: translateX($menuwidth);
    }
  }

  .mobile-menu-underlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    background-color: black;
    z-index: 950;
    opacity: 0.5;
    transition: transform 0.25s ease;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
}
</style>
